import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { useIsMobile } from '../../lib/useIsMobile';
import { useEscapeKey } from '../../lib/useEscapeKey';

interface PanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /**
   * Panel variant:
   * - "modal": Centered dialog (desktop), bottom sheet (mobile)
   * - "drawer": Right sidebar (desktop), bottom sheet (mobile)
   */
  variant?: 'modal' | 'drawer';
  /**
   * Size for modal variant (ignored for drawer)
   * - sm: max-w-md
   * - md: max-w-2xl
   * - lg: max-w-4xl
   * - xl: max-w-6xl
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
};

/**
 * Unified Panel component for modals and drawers.
 *
 * Design:
 * - Solid white (light) / slate-900 (dark) background
 * - Mobile: Always bottom sheet with drag indicator
 * - Desktop modal: Centered with backdrop, configurable size
 * - Desktop drawer: Right sidebar (320px width)
 * - Framer Motion animations for smooth transitions
 */
export function Panel({
  isOpen,
  onClose,
  title,
  children,
  variant = 'modal',
  size = 'lg',
}: PanelProps) {
  const isMobile = useIsMobile();

  // Lock body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEscapeKey(onClose, isOpen);

  const panelContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-40"
          />

          {isMobile ? (
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed inset-x-0 bottom-0 max-h-[85vh] bg-gradient-to-b from-gray-50 to-white dark:from-neutral-950 dark:to-neutral-900 rounded-t-2xl shadow-2xl z-50 flex flex-col border-t border-gray-200 dark:border-white/10"
            >
              <div className="flex justify-center pt-3 pb-1 shrink-0">
                <div className="w-10 h-1 bg-gray-300 dark:bg-slate-600 rounded-full" />
              </div>

              <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-200 dark:border-white/10 shrink-0">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <CloseIcon />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {children}
              </div>
            </motion.div>
          ) : variant === 'modal' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
            >
              <div
                className={`relative w-full ${sizeClasses[size]} bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col border border-gray-200 dark:border-white/10 pointer-events-auto`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10 shrink-0">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {title}
                  </h2>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors text-gray-600 dark:text-slate-400"
                    aria-label="Close panel"
                  >
                    <CloseIcon />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  {children}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ x: 320, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 320, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-80 bg-gradient-to-b from-gray-50 to-white dark:from-neutral-950 dark:to-neutral-900 shadow-2xl z-50 flex flex-col border-l border-gray-200 dark:border-white/10"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-white/10 shrink-0">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <CloseIcon />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {children}
              </div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(panelContent, document.body);
}

function CloseIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
